import API, { graphqlOperation } from "@aws-amplify/api";
import moment from "moment";
import {
  execRead,
  execWrite,
  execReadBySortkey,
  execReadByPK,
} from "./DBService";
import * as scheduleQueries from "./ScheduleQueries";
import * as queries from "../graphql/queries";
import { utcToZonedTime, zonedTimeToUtc, format, toDate } from "date-fns-tz";
import { createProviderSchedule } from "../graphql/mutations";
import { v4 as uuid } from "uuid";
import { stripLocationPrefix } from "../utils";
import { addDays } from "date-fns";
import { uniq, sortBy, min } from "lodash";
import { mod } from "../utils";
import { calculateTurfDistance } from "../utils/turfUtils";
//TODO: Timezone has to be Location timezone or defaule timezone at company level
//TODO: Move date functions to its own DateUtil module
//The dates passed into the ScheduleService will be considered to be Location Timezone or Company Timezone
const SLOTSINADAY = 288;
const SLOTUNITMINS = 5;
const SLOTSINHOUR = 12;
const TIMEMAP = buildTimeMap();
const SCHEDPKSKSPLITAT = "::";
const NOTRAVELTIMERADIUS = 1000; //in meters
const EARTHRADIUS = 6371e3; //in meters
const ALLDAYSARRAY = [0, 1, 2, 3, 4, 5, 6];
const BATCH_SIZE = 25;
export const DEFAULT_LOOKAHEAD_DAYS_FOR_CLUSTERING = 7;
const DEFAULT_MAX_NUM_OF_SUGGESTED_SLOTS_TO_DISPALY = 10;

function weekdaysString(wdArray) {
  return "" + wdArray.join("");
}
// use API
const createTimeblock = async ({
  companyId,
  locationId,
  startDate,
  endDate,
  startTime,
  endTime,
  type,
  status,
  weeksToRepeat,
  weekDays,
  tz,
  sdtutc,
  providerId,
  geoLoc,
}) => {
  const weekdaysStr = weekDays ? weekdaysString(weekDays) : weekDays;

  const timeblockData = {
    id: getProviderScheduleId(companyId, providerId),
    scheduleinfo: `BK|${startDate} ${startTime}|${locationId}|${uuid()}`,
    companyId,
    startDate,
    endDate,
    startTime,
    endTime,
    type,
    status,
    tz,
    active: true,
    providerId,
    providerScheduleProviderId: providerId,
    ...(weekdaysStr && { weekDays: weekdaysStr }),
    ...(weeksToRepeat && { weeksToRepeat }),
    sdtutc,
    locations: JSON.stringify([`${locationId}`]),
    ...(geoLoc?.lat && { latitude: geoLoc.lat }),
    ...(geoLoc?.lng && { longitude: geoLoc.lng }),
  };
  let response = await execWrite({
    opname: "createProviderSchedule",
    op: createProviderSchedule,
    input: timeblockData,
  });
  if (response) {
    response = {
      ...response,
      id: `${response.id}${SCHEDPKSKSPLITAT}${response.scheduleinfo}`,
    };
  }
  return response;
};
const getSlotsForSbs = async ({
  SBs,
  startDate, // TODO: change the format to regular date from aws date
  numberOfDays,
  bookingIntervalMinutes,
  serviceDuration,
  travelTime,
  geoLoc,
  bookingIncrement,
  providerTz,
  locationTz,
  companyId,
  providerId,
  addStats = false,
  returnUnreadable = false,
  cachedBUTs,
}) => {
  const timeZoneAdjustedScheduleBlocks = adjustScheduleBlocksToLocationTz(
    providerTz,
    locationTz,
    SBs,
  );
  const nowInLocalTimeZone = utcToZonedTime(new Date(), locationTz);
  const startingDate = startDate ? startDate : getAWSDate(nowInLocalTimeZone);
  const endingDate = getEndingDate(startingDate, numberOfDays);
  const primaryKey = getProviderScheduleId(companyId, providerId);

  const rawBUTs = cachedBUTs
    ? cachedBUTs.map((but) => ({ ...but }))
    : (await execReadBySortkey({
        opname: "listProviderSchedules",
        op: scheduleQueries.listAllScheduleItems,
        id: { id: primaryKey },
        skey: { scheduleinfo: { beginsWith: `BUT|` } },
        filter: { deleted: { ne: true } },
        limit: 500,
      })).items;

  if (rawBUTs && rawBUTs.length)
    adjustBUTimesForBookingLocationTimezone(rawBUTs, providerTz, locationTz);
  const BUTs = rawBUTs;

  const bookedResp = await execReadBySortkey({
    opname: "listProviderSchedules",
    op: scheduleQueries.listTimeblocks,
    id: { id: primaryKey },
    skey: {
      scheduleinfo: { between: [`BK|${startingDate}`, `BK|${endingDate}`] },
    },
    filter: { status: { ne: "CANCELLED" } },
    limit: 500,
  });
  if (bookedResp && bookedResp.items) {
    const found = bookedResp.items.find((bk) =>
      bk.scheduleinfo.includes("|CL-"),
    );
    if (found) {
      //booking at company location found, so get geoLocs of the company locations
      const companyLocations = await getCompanyLocations(companyId);
      for (const companyLoc of companyLocations) {
        for (const booking of bookedResp.items) {
          if (
            booking.scheduleinfo.includes(companyLoc.id) &&
            companyLoc.latitude &&
            companyLoc.longitude
          ) {
            if (!booking.latitude && !booking.longitude) {
              booking.latitude = companyLoc.latitude;
              booking.longitude = companyLoc.longitude;
            }
          }
        }
      }
    }
  }

  if (bookedResp && bookedResp.items) {
    // let locationTz = "America/Vancouver";
    adjustGlobalBookingTimesForBookingLocationTimezone(
      locationTz,
      bookedResp.items,
      serviceDuration,
    );
  }

  const unreadableSlots = computeAvailableSlots(
    [...timeZoneAdjustedScheduleBlocks, ...BUTs],
    bookedResp.items,
    awsDateToJsDate(startingDate),
    numberOfDays,
    bookingIncrement,
    travelTime,
    geoLoc,
    locationTz,
  );

  const readableSlots = toReadableSlots(
    null,
    unreadableSlots.filter((urs) => urs.hasavail),
    bookingIncrement,
    serviceDuration,
  );

  const futureSlotsOnly = filterOutTodaysPastSlots(
    readableSlots,
    bookingIntervalMinutes,
    nowInLocalTimeZone,
  );
  return returnUnreadable
    ? [unreadableSlots, futureSlotsOnly, bookedResp.items]
    : futureSlotsOnly;
};

async function getCompanyLocations(companyId) {
  try {
    let locationData = await execReadByPK({
      opname: "companyLocationByCompany",
      op: /* GraphQL */ `
        query CompanyLocationByCompany(
          $companyId: String
          $locationnameCreatedAt: ModelCompanyLocationByCompanyCompositeKeyConditionInput
          $sortDirection: ModelSortDirection
          $filter: ModelCompanyLocationFilterInput
          $limit: Int
          $nextToken: String
        ) {
          companyLocationByCompany(
            companyId: $companyId
            locationnameCreatedAt: $locationnameCreatedAt
            sortDirection: $sortDirection
            filter: $filter
            limit: $limit
            nextToken: $nextToken
          ) {
            items {
              id
              locationname
              longitude
              latitude
              timezone
              virtual
              active
              deleted
            }
            nextToken
          }
        }
      `,
      id: {
        companyId,
      },
      limit: 500,
    });

    if (locationData?.items && locationData?.items.length > 0) {
      return locationData.items;
    }
    return [];
  } catch (e) {
    console.log("error in ScheduleService getCompanyLocations", e);
    return [];
  }
}

const adjustScheduleBlocksToLocationTz = (fromTz, targetTz, blocks) =>
  targetTz !== fromTz
    ? blocks.map(
        ({ startTime, startDate, endDate, endTime, weekDays, ...rest }) => {
          // make dates in the original tz
          const startDateTimeInScheduleTz = toDate(
            `${startDate}T${startTime}`,
            {
              timeZone: fromTz,
            },
          );
          const endDateTimeInScheduleTz = toDate(`${endDate}T${endTime}`, {
            timeZone: fromTz,
          });
          // convert to target tz
          const startDateTimeInLocationTz = utcToZonedTime(
            startDateTimeInScheduleTz,
            targetTz,
          );
          const endDateTimeInLocationTz = utcToZonedTime(
            endDateTimeInScheduleTz,
            targetTz,
          );
          //format
          const startDateInLocationTz = format(
            startDateTimeInLocationTz,
            "yyyy-MM-dd",
          );
          const startTimeInLocationTz = format(
            startDateTimeInLocationTz,
            "HH:mm",
          );

          const endDateInLocationTz = format(
            endDateTimeInLocationTz,
            "yyyy-MM-dd",
          );
          const endTimeInLocationTz = format(endDateTimeInLocationTz, "HH:mm");

          const adjustedWeekDays =
            endTimeInLocationTz < startTimeInLocationTz //straddle two days case
              ? uniq(
                  weekDays
                    .split("")
                    .reduce(
                      (agr, dayNum) =>
                        agr.concat(
                          mod(
                            parseInt(dayNum) +
                              1 * (compareTimezones(targetTz, fromTz) ? 1 : -1),
                            7,
                          ).toString(),
                        ),
                      weekDays,
                    ),
                ).join("")
              : startDateInLocationTz !== startDate
                ? uniq(
                    // move case
                    weekDays
                      .split("")
                      .map((dayNum) =>
                        mod(
                          parseInt(dayNum) +
                            1 * (compareTimezones(targetTz, fromTz) ? 1 : -1),
                          7,
                        ).toString(),
                      ),
                  ).join("")
                : weekDays;
          return {
            ...rest,
            weekDays: adjustedWeekDays,
            startTime: startTimeInLocationTz,
            startDate: startDateInLocationTz,
            endTime: endTimeInLocationTz,
            endDate: endDateInLocationTz,
          };
        },
      )
    : blocks;

function adjustGlobalBookingTimesForBookingLocationTimezone(
  locationTz,
  bookings,
) {
  for (let b of bookings) {
    if (b.sdtutc && b.startTime && b.endTime) {
      const bookedMinutes = calculateBookedMinutes(b.startTime, b.endTime);
      const startDateUTC = new Date(b.sdtutc);
      const startDateInLocZone = utcToZonedTime(startDateUTC, locationTz);
      b.startDate = format(startDateInLocZone, "yyyy-MM-dd", {
        timeZone: locationTz,
      });
      b.startTime = format(startDateInLocZone, "HH:mm", {
        timeZone: locationTz,
      });
      const endDateUTC = new Date(startDateInLocZone);
      endDateUTC.setMinutes(endDateUTC.getMinutes() + bookedMinutes);
      b.endTime = format(endDateUTC, "HH:mm", locationTz);
    }
  }
}

function adjustBUTimesForBookingLocationTimezone(BUTs, providerTz, locationTz) {
  for (let sbu of BUTs) {
    const startDateTimeinUtc = zonedTimeToUtc(
      `${sbu.startDate}T${sbu.startTime}:00`,
      providerTz,
    );
    const startDateTimeInBookingLoc = utcToZonedTime(
      startDateTimeinUtc,
      locationTz,
    );
    sbu.startDate = format(startDateTimeInBookingLoc, "yyyy-MM-dd");
    sbu.startTime = format(startDateTimeInBookingLoc, "HH:mm");

    const endDateTimeinUtc = zonedTimeToUtc(
      `${sbu.endDate}T${sbu.endTime}:00`,
      providerTz,
    );
    const endDateTimeInBookingLoc = utcToZonedTime(
      endDateTimeinUtc,
      locationTz,
    );
    sbu.endDate = format(endDateTimeInBookingLoc, "yyyy-MM-dd");
    sbu.endTime = format(endDateTimeInBookingLoc, "HH:mm");
  }
}

function calculateBookedMinutes(bkstartTime, bkendTime) {
  //this works only if start and end times are within the same date
  //Jan 10th 11pm to Jan 11th 1am won't work.
  const startHHMM = bkstartTime.split(":");
  const endHHMM = bkendTime.split(":");
  const startTime = new Date(2000, 1, 1, startHHMM[0], startHHMM[1]);
  const endTime = new Date(2000, 1, 1, endHHMM[0], endHHMM[1]);
  const diff = endTime.valueOf() - startTime.valueOf();
  const bookedMinutes = diff / (1000 * 60);
  return bookedMinutes;
}

function filterOutTodaysPastSlots(
  slots,
  bookingIntervalMinutes,
  nowInLocalTimeZone,
) {
  const checkDateTime = new Date(
    nowInLocalTimeZone.getTime() + bookingIntervalMinutes * 60 * 1000,
  );
  slots.forEach((day) => {
    day.slots = day.slots.filter((slot) =>
      isFutureSlot_v3(slot, day.date, checkDateTime),
    );
  });

  return slots;
}

function filterByTimesofday(slots, timesofday) {
  slots.forEach((day) => {
    day.slots = day.slots.filter((slot) => timesofday[slot.tfd]);
  });

  return slots;
}

function isFutureSlot_v3(slot, dayDate, checkDateTime) {
  //Calculate millis of the slot
  const dayDateMillis = dayDate.getTime();
  let slotDate = new Date(dayDateMillis);
  slotDate.setHours(Number.parseInt(slot.start24.slice(0, 2)));
  slotDate.setMinutes(Number.parseInt(slot.start24.slice(-2)));
  return slotDate.getTime() > checkDateTime.getTime();
}

function computeAvailableSlots(
  timeBlocks,
  booked,
  selectedStartDateTime,
  nd,
  bookingIncrement,
  travelTime,
  geoLoc,
  locationTz,
) {
  travelTime = travelTime ? travelTime : 0;

  //Create the placeholder array of by-date slots objects for asked numdays
  //For earch day create slots array based on step (15,30,60 mins)
  let slotMapObjects = new Array(nd).fill(null).map((a, dayIndex) => ({
    date: addDays(selectedStartDateTime, dayIndex),
    hasavail: false,
    slotmap: Array.from({ length: SLOTSINADAY }),
    availableIntervals: [],
  }));

  //iterate over the AVAILABLE timeblocks
  //  for each AVAILABLE timeblock
  //    based on startTime and endTime (and step), calculate start index and stop index to be marked as 1 (meaning available)
  //    [TBD: it is possible to have this number higher than 1 indicating that multiple booking is possible but need to design the feature end-to-end]
  //     Once start and stop indices are calculated, for each day in the slotMapArray which falls within the AVAILABLE timeblock's range
  //     and it is the workingday of the provider, fill the availability in each day's slotmap

  timeBlocks
    .filter((timeblock) => timeblock.type === "AVAILABLE")
    .forEach(({ weekDays, startTime, startDate, endDate, endTime }) => {
      slotMapObjects
        .filter((slotMapObject) => {
          return (
            slotMapObject.date >= awsDateToJsDate(startDate) &&
            slotMapObject.date <= awsDateToJsDate(endDate) &&
            worksonday(slotMapObject.date, weekDays)
          );
        })
        .forEach((slotMapObject) => {
          slotMapObject.hasavail = true;
          const availableIntervalsForCurrentBlock =
            endTime >= startTime
              ? [
                  // regular interval from start time to end  time
                  [getIndexByTime(startTime), getIndexByTime(endTime)],
                ]
              : [
                  // schedule is split between two days because of time zone so two intervals from start of day till end time (the piece from last day)
                  // and from start time till end of day
                  ...(workedTheDayBefore(slotMapObject.date, weekDays)
                    ? [[0, getIndexByTime(endTime)]]
                    : []),
                  ...(workedTheDayAfter(slotMapObject.date, weekDays)
                    ? [[getIndexByTime(startTime), SLOTSINADAY]]
                    : []),
                ];
          // create the map from available intervals
          const slotsForCurrentBlock = Array.from(
            { length: SLOTSINADAY },
            (val, index) =>
              availableIntervalsForCurrentBlock.some(
                (availableInterval) =>
                  index >= availableInterval[0] &&
                  index <= availableInterval[1],
              )
                ? 1
                : undefined,
          );
          // merge current block slots into the slots array for that day
          slotMapObject.slotmap = slotMapObject.slotmap.map((val, i) =>
            val === undefined && slotsForCurrentBlock[i] === undefined
              ? undefined
              : val || slotsForCurrentBlock[i],
          );
          slotMapObject.availableIntervals =
            slotMapObject.availableIntervals.concat(
              availableIntervalsForCurrentBlock,
            );
        });
    });

  //TODO: do we need to check for UNAVAILABLE?
  timeBlocks
    .filter((timeblock) => timeblock.type === "UNAVAILABLE")
    .forEach((timeblock) => {
      slotMapObjects
        .filter(
          (slotMapObject) =>
            slotMapObject.hasavail &&
            hasUnavailability(
              slotMapObject.date,
              awsDateToJsDate(timeblock.startDate),
              awsDateToJsDate(timeblock.endDate),
              timeblock.weekDays,
            ),
        )
        .forEach((slotMapObject) => {
          markUnAvailability(
            slotMapObject.slotmap,
            getIndexByTime(timeblock.startTime),
            getIndexByTime(timeblock.endTime),
            0, //Travel timee zero for
            null,
            null,
            bookingIncrement,
          );
        });
    });
  // iterate over BOOKED blocks (appointments already booked)
  // For each booked appointment,
  //    compute start and stop index based on the start and end time of the appointment
  //    get the date of the appointment
  //    find the day in slotMapArray that matches the appointment date
  //    for the matched day in the slotMapArray, mark the slotmap's elements as booked (i.e. unavailable)
  booked
    .filter(({ type }) => type === "BOOKED")
    .forEach((timeblock) => {
      slotMapObjects
        .filter(
          (slotmap) =>
            slotmap.hasavail &&
            slotmap.date.getTime() ===
              awsDateToJsDate(timeblock.startDate).getTime(),
        )
        .forEach((slotmap) => {
          markUnAvailability(
            slotmap.slotmap,
            getIndexByTime(timeblock.startTime),
            getIndexByTime(timeblock.endTime),
            travelTime,
            timeblock,
            geoLoc,
            bookingIncrement,
          );
        });
    });
  return slotMapObjects;
}

function hasUnavailability(
  slotmapDate,
  unavStartDate,
  unavEndDate,
  unavWeekDays,
) {
  // if SBU block has weekdays, weekday must match
  // otherwise only date match is required

  if (
    slotmapDate.getTime() >= unavStartDate.getTime() &&
    slotmapDate.getTime() <= unavEndDate.getTime()
  ) {
    if (unavWeekDays && unavWeekDays.length) {
      let unavaOnWeekday =
        unavWeekDays.indexOf(slotmapDate.getDay()) !== -1 ? true : false;
      if (!unavaOnWeekday) return false;
    }
    return true;
  } else return false;
}
//sma slotMapArray, step 15, 30, 60 mins
function toReadableSlots(schedule, sma, step, serviceDuration) {
  const readableArray = [];
  sma.forEach((d) => {
    readableArray.push({
      // schid: schedule.id,
      date: d.date,
      slots: toReableArray(
        d.slotmap,
        step,
        d.date,
        d.availableIntervals,
        serviceDuration,
      ),
    });
  });
  return readableArray;
}
//slotmap array of 0s and 1s indicating the availability
//convert info in the slotmap into the start times of the available slots
function toReableArray(
  slotmap,
  step,
  date,
  availableIntervals,
  serviceDuration,
) {
  const readableSlots = [];
  const displaybleSlots = displaySlotsByStep2(
    step,
    slotmap,
    TIMEMAP,
    serviceDuration,
    availableIntervals,
  );
  if (displaybleSlots.length > 0) {
    displaybleSlots.forEach((ds) => {
      const datetime = new Date(date.getTime());
      datetime.setHours(ds.hr);
      datetime.setMinutes(ds.mins);
      readableSlots.push({
        len: step,
        start24: ds.tstr24,
        start12: ds.tstr12,
        datetime,
        tfd: ds.tfd,
        hr: ds.hr,
      });
    });
  }

  return readableSlots;
}

function getAWSDate(date) {
  let oy = { year: "numeric" };
  let YYYY = date.toLocaleDateString("en-US", oy);
  let om = { month: "2-digit" };
  let MM = date.toLocaleDateString("en-US", om);
  let od = { day: "2-digit" };
  let DD = date.toLocaleDateString("en-US", od);
  return `${YYYY}-${MM}-${DD}`;
}

function getEndingDate(sd, nd) {
  const ed = awsDateToJsDate(sd);
  ed.setDate(ed.getDate() + nd);
  return getAWSDate(ed);
}

function awsDateToJsDate(awsDate) {
  // from YYYY-MM-DD to local timezone
  const dateparts = awsDate.split("-");
  return new Date(
    parseInt(dateparts[0]),
    parseInt(dateparts[1] - 1),
    parseInt(dateparts[2]),
  ); //yes, local time
}

function awsDateToJsEndDate(awsDate) {
  const d = awsDateToJsDate(awsDate);
  d.setHours(23);
  d.setMinutes(59);
  d.setSeconds(59);
  d.setMilliseconds(999);
  return d;
}

function worksonday(sample, range) {
  if (range && sample) {
    return range.indexOf(sample.getDay()) !== -1 ? true : false;
  } else return true; //no range, so weekday does not matter.
}
const workedTheDayBefore = (day, shceduleWeekday) =>
  shceduleWeekday.indexOf(mod(day.getDay() - 1, 7)) !== -1;

const workedTheDayAfter = (day, shceduleWeekday) =>
  shceduleWeekday.indexOf(mod(day.getDay() + 1, 7)) !== -1;

function areSame(d1, d2) {
  return (
    d1 &&
    d2 &&
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
}
// aDate - appointment date and time to check for validity
// dayCount - number associated with the unit in dayType
// dayType - days, weeks, months
const validateApptDate = async (
  aDate,
  scheduleIds,
  dayCount,
  dayType,
  bookingIntervalMinutes,
  serviceDuration,
  travelTime,
  geoLoc,
  bookingIncrement,
  selectedLocation,
  companyId,
  provider,
  { cachedSBs, cachedBUTs } = {},
) => {
  travelTime = travelTime ? travelTime : 0;
  const { id: providerId, timezone: providerTz } = provider;
  //Get timeblocks
  const availableTBs = cachedSBs
    ? cachedSBs
    : await getSBsByScheduleInfo(
        getProviderScheduleId(companyId, providerId),
        scheduleIds,
        {
          locationId: selectedLocation?.id,
          isRemoteLocation: !selectedLocation?.id,
        },
      );
  //if apptDate's day has the schedule
  const doesworkondate = isScheduleAvailableOnApptDate(aDate, availableTBs);
  if (doesworkondate.result) {
    //provider works on the booking date
    /////....... start of new Code
    const slotsByDate = await getSlotsForSbs({
      SBs: availableTBs,
      startDate: getAWSDate(aDate),
      numberOfDays: 1,
      bookingIntervalMinutes,
      serviceDuration,
      travelTime,
      geoLoc,
      bookingIncrement,
      selectedLocation,
      companyId,
      providerId,
      providerTz,
      locationTz: selectedLocation.timezone,
      cachedBUTs,
    });
    const apptTime24hr = get24hrTime(aDate);
    for (const day of slotsByDate) {
      if (day && areSame(day.date, aDate))
        for (const slot of day.slots) {
          if (slot.start24 === apptTime24hr) {
            return {
              isValid: true,
              hasAlternate: false,
              alternateSlot: {},
              originalSlot: aDate,
              dateInSchedule: doesworkondate.dateInSchedule,
            };
          }
        }
    }
    for (const day of slotsByDate) {
      if (day && areSame(day.date, aDate)) {
        if (day && day.slots && day.slots.length) {
          for (const slot of day.slots) {
            slot.diff = Math.abs(slot.datetime.getTime() - aDate.getTime());
          }
          day.slots.sort(function (a, b) {
            return a.diff - b.diff;
          });
          return {
            isValid: false,
            hasAlternate: true,
            alternateSlot: { date: day.date, slot: day.slots[0] },
            originalSlot: aDate,
            dateInSchedule: doesworkondate.dateInSchedule,
          };
        } else {
          return {
            isValid: false,
            hasAlternate: false,
            alternateSlot: {},
            originalSlot: aDate,
            dateInSchedule: doesworkondate.dateInSchedule,
          };
        }
      }
    }
    ///// End of new Code.....................
  } else {
    //suggest alternate

    //Get slots for one week
    const slotSearchDateRange = decideAlternateSlotSearchDateRange(
      aDate,
      dayCount,
      dayType,
    );
    if (slotSearchDateRange.result === false) {
      return {
        isValid: false,
        hasAlternate: false,
        alternateSlot: {},
        originalSlot: aDate,
        dateInSchedule: doesworkondate.dateInSchedule,
      };
    }
    const slotsBySchedule = await getSlotsForSbs({
      SBs: availableTBs,
      startDate: getAWSDate(slotSearchDateRange.startDate),
      numberOfDays: slotSearchDateRange.numdays || 7,
      bookingIntervalMinutes,
      serviceDuration,
      travelTime,
      geoLoc,
      bookingIncrement,
      selectedLocation,
      companyId,
      providerId,
      providerTz,
      locationTz: selectedLocation.timezone,
      cachedBUTs,
    });
    const apptTime24hr = get24hrTime(aDate);
    // Find the closest alternate slot across all days in the search range
    for (const day of slotsBySchedule) {
      if (day && day.slots && day.slots.length) {
        // First try exact time match on a nearby day
        for (const slot of day.slots) {
          if (slot.start24 === apptTime24hr) {
            return {
              isValid: false,
              hasAlternate: true,
              alternateSlot: { date: day.date, slot: slot },
              originalSlot: aDate,
              dateInSchedule: doesworkondate.dateInSchedule,
            };
          }
        }
      }
    }
    // If no exact time match, find the closest slot by time difference
    for (const day of slotsBySchedule) {
      if (day && day.slots && day.slots.length) {
        for (const slot of day.slots) {
          slot.diff = Math.abs(slot.datetime.getTime() - aDate.getTime());
        }
        day.slots.sort((a, b) => a.diff - b.diff);
        return {
          isValid: false,
          hasAlternate: true,
          alternateSlot: { date: day.date, slot: day.slots[0] },
          originalSlot: aDate,
          dateInSchedule: doesworkondate.dateInSchedule,
        };
      }
    }
  }
  return {
    isValid: false,
    hasAlternate: false,
    alternateSlot: {},
    originalSlot: aDate,
    dateInSchedule: doesworkondate.dateInSchedule,
  };
};

const decideAlternateSlotSearchDateRange = (apptDate, dayCount, dayType) => {
  if (dayType === "days" && dayCount === 1) {
    //No alternate slot can be searched
    return { result: false };
  }
  if (dayType === "days") {
    return {
      result: true,
      startDate: addDays(apptDate, 1),
      numdays: dayCount === 2 ? 0 : dayCount - 1,
    };
  }
  if (dayType === "weeks") {
    return {
      result: true,
      startDate: addDays(apptDate, 1),
      numdays: dayCount === 1 ? 3 : (dayCount * 7) / 2,
    };
  }
  if (dayType === "months") {
    return {
      result: true,
      startDate: addDays(apptDate, 1),
      numdays: dayCount === 1 ? 14 : (dayCount * 30) / 2,
    };
  }
  return {
    result: false,
  };
};

const isScheduleAvailableOnApptDate = (aDate, avTBs) => {
  const ret = {
    result: false,
    dosuggestalternate: false,
    dateInSchedule: false,
  };
  let dateInSchedule = false;
  for (let tb of avTBs) {
    if (
      aDate >= awsDateToJsDate(tb.startDate) &&
      aDate <= awsDateToJsEndDate(tb.endDate)
    ) {
      dateInSchedule = true;
    }
  }
  ret.dateInSchedule = dateInSchedule;
  for (let tb of avTBs) {
    if (
      aDate >= awsDateToJsDate(tb.startDate) &&
      aDate <= awsDateToJsEndDate(tb.endDate)
    ) {
      if (worksonday(aDate, tb.weekDays)) {
        ret.result = true;
        ret.dosuggestalternate = false;
      } else {
        if (!ret.result) {
          ret.result = false;
          ret.dosuggestalternate = true;
        }
      }
    } else {
      if (!ret.result) {
        ret.result = false;
        ret.dosuggestalternate = false;
      }
    }
  }
  return ret;
};

const getSBsByScheduleInfo = async (pk, schInfos, { locationId, isRemoteLocation } = {}) => {
  const { items: scheduleBlocks } = await execReadBySortkey({
    opname: "listProviderSchedules",
    op: scheduleQueries.listTimeblocks,
    id: { id: pk },
    skey: { scheduleinfo: { beginsWith: `SB|` } },
    filter: {
      and: [{ active: { ne: false } }, { deleted: { ne: true } }],
    },
    limit: 500,
  });
  let filteredBlocks = scheduleBlocks.filter(({ scheduleinfo }) =>
    schInfos.some((schInfos) => scheduleinfo.indexOf(schInfos) !== -1),
  );
  // Filter SBs by location if a location context is provided
  if (locationId && filteredBlocks.length) {
    const locationKey = isRemoteLocation
      ? `PL-${locationId}`
      : `CL-${locationId}`;
    const locationFiltered = filteredBlocks.filter(
      (sb) => sb.locations && sb.locations.includes(locationKey),
    );
    // Only apply the filter if it yields results — avoid
    // wiping out all SBs when location data is missing on older records
    if (locationFiltered.length) {
      filteredBlocks = locationFiltered;
    }
  }
  if (filteredBlocks) {
    for (let tb of filteredBlocks) {
      createScheduleDescription(tb);
    }
  }
  return filteredBlocks;
};

const fetchBUTs = async (pk, { endDateFilter } = {}) => {
  const filter = endDateFilter
    ? {
        and: [
          { deleted: { ne: true } },
          { startDate: { le: endDateFilter } },
        ],
      }
    : { deleted: { ne: true } };
  const { items } = await execReadBySortkey({
    opname: "listProviderSchedules",
    op: scheduleQueries.listAllScheduleItems,
    id: { id: pk },
    skey: { scheduleinfo: { beginsWith: `BUT|` } },
    filter,
    limit: 500,
  });
  return items;
};

function createScheduleDescription(tb) {
  tb.timeRange = `${withAMPM(tb.startTime)} — ${withAMPM(tb.endTime)}`;
  tb.dateRange = `${formatted(tb.startDate)} — ${formatted(tb.endDate)}`;
  const weekDaysArr =
    tb?.weekDays === "" ? ALLDAYSARRAY : tb.weekDays.split("");
  if (weekDaysArr) {
    weekDaysArr.sort();
    let repeat = "";
    if (weekDaysArr.length === 7) {
      repeat = "Every day";
    } else if (weekDaysArr.length === 5 && isWeekdaysOnly(weekDaysArr)) {
      repeat = "Every weekday";
    } else {
      const weeks = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
      const sortedWeekDays = [...new Set(weekDaysArr)].sort((a, b) => a - b);
      if (sortedWeekDays.length === 1) {
        repeat = `${weeks[sortedWeekDays[0]]}`;
      } else if (sortedWeekDays.length === 7) {
        repeat = `Every day`;
      } else {
        const firstDay = weeks[sortedWeekDays[0]];
        const lastDay = weeks[sortedWeekDays[sortedWeekDays.length - 1]];
        repeat = `${firstDay} — ${lastDay},`;
      }
    }
    tb.repeat = repeat;
  }
}

const sortScheduleDisplayOrder = (schedules) => {
  const allDays = "0123456";
  const allWeekDays = "12345";
  return sortBy(schedules, [
    (schedule) =>
      schedule.weekDays === allWeekDays || schedule.weekDays === allDays
        ? -1
        : 1, //move "Every weekday" or "Every day" to the top
    (schedule) => {
      const days = schedule.weekDays.split("").map(Number);
      return min(days.map((day) => (day === 0 ? 7 : day))); //treat Sunday (0) as 7 for sorting
    },
    "startTime", //sort by start time if days are the same
  ]);
};

function isWeekdaysOnly(wa) {
  wa.sort();
  for (let i = 0; i <= 4; i++) {
    if (!(parseInt(wa[i]) === i + 1)) return false;
  }
  return true;
}
function formatted(d) {
  return new Date(d).toLocaleDateString("en-US", {
    year: "2-digit",
    month: "short",
    day: "numeric",
  });
}

function withAMPM(t) {
  if (!t) return "";
  const [hh, mm] = t.split(":");
  let hours = parseInt(hh, 10);
  const ampm = hours < 12 ? "AM" : "PM";
  hours = hours % 12 || 12;
  return `${hours}:${mm} ${ampm}`;
}

const getProviderLocation = async (id) => {
  const data = await API.graphql(
    graphqlOperation(
      `query GetProviderLocation($id: ID!) {
      getProviderLocation(id: $id) {
        id
        name 
        timezone
      }
    }`,
      {
        id,
      },
    ),
  );
  return data.data.getProviderLocation;
};

const getCompanyLocationGeoCoordinates = async (scheduleinfo) => {
  try {
    if (scheduleinfo && scheduleinfo.includes("|CL-")) {
      const parts = scheduleinfo.split("|");
      const locationId = parts[1].slice(3);
      const companyLocation = await getCompanyLocation(locationId);
      if (companyLocation) {
        return {
          lat: companyLocation.latitude,
          lng: companyLocation.longitude,
        };
      }
    }
    return null;
  } catch (e) {
    return null;
  }
};
const getCompanyLocation = async (id) => {
  const data = await API.graphql(
    graphqlOperation(
      `query GetCompanyLocation($id: ID!) {
        getCompanyLocation(id: $id) {
        id
        locationname 
        timezone
        latitude
        longitude
      }
    }`,
      {
        id,
      },
    ),
  );
  return data.data.getCompanyLocation;
};

const get24hrTime = (aDate) => {
  return aDate
    ? aDate.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h24",
      })
    : "";
};
function getIndexByTime(t) {
  const parts = t.split(":");
  const h = parseInt(parts[0]);
  const m = parseInt(parts[1]);
  const index = (h * 60 + m) / SLOTUNITMINS;
  return index;
}

function markUnAvailability(
  sm,
  si,
  ei,
  travelTime,
  tb,
  geoLoc,
  bookingIncrement,
) {
  if (tb && tb.latitude && tb.longitude && geoLoc) {
    if (!includeTravelTime(tb, geoLoc)) travelTime = 0;
  }
  if (travelTime && travelTime > 0) {
    let ttIndLen = travelTime / SLOTUNITMINS;
    si = si - ttIndLen;
    ei = ei + ttIndLen;
    let clawbackInd = ei % (bookingIncrement / SLOTUNITMINS);
    if (clawbackInd <= 1) {
      //10 mins into the next half an hour like 10:10 or 10:40
      ei = ei - clawbackInd;
    }
  }
  //in above block ei could correspond to endTime 00:00 i.e. ei=0 (or near midnight like 11:50pm), and hence its travel time adjusted ei could be between 0 and 5 of the NEXT  DAY.
  // we have no way of storing and presenting this fact to users so reset the endTime back 00:00
  if (ei < si) ei = SLOTSINADAY;

  for (let s = si; s <= ei - 1; s++) {
    sm[s] = 0;
  }
}

function includeTravelTime(tb, geoLoc) {
  if (tb && tb.latitude && tb.longitude && geoLoc) {
    const tbLat = Number(tb.latitude);
    const tbLng = Number(tb.longitude);
    const bookLat = Number(geoLoc.lat);
    const bookLng = Number(geoLoc.lng);
    const lat = tbLat - bookLat;
    const lng = tbLng - bookLng;
    const dLat = (lat * Math.PI) / 180;
    const dLng = (lng * Math.PI) / 180;
    const bookLatRad = (bookLat * Math.PI) / 180;
    const tbLatRad = (tbLat * Math.PI) / 180;

    let a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(bookLatRad) *
        Math.cos(tbLatRad) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let d = EARTHRADIUS * c;
    if (d < NOTRAVELTIMERADIUS) return false;
  }
  return true;
}

function calcDist(fromGeo, toGeo) {
  try {
    if (
      fromGeo.latitude &&
      fromGeo.longitude &&
      toGeo.latitude &&
      toGeo.longitude
    ) {
      if (
        isNaN(fromGeo.latitude) ||
        isNaN(fromGeo.longitude) ||
        isNaN(toGeo.latitude) ||
        isNaN(toGeo.longitude)
      )
        return { km: 0, miles: 0, usable: false };
      const fromLat = Number(fromGeo.latitude);
      const fromLng = Number(fromGeo.longitude);
      const toLat = Number(toGeo.latitude);
      const toLng = Number(toGeo.longitude);
      const lat = fromLat - toLat;
      const lng = fromLng - toLng;
      const dLat = (lat * Math.PI) / 180;
      const dLng = (lng * Math.PI) / 180;
      const toLatRad = (toLat * Math.PI) / 180;
      const fromLatRad = (fromLat * Math.PI) / 180;

      let a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toLatRad) *
          Math.cos(fromLatRad) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      let d = EARTHRADIUS * c;
      if (d === 0) return { km: 0, miles: 0, usable: false };
      let km = round(Number(d / 1000), 1);
      let miles = round(Number((d / 1000) * 0.621371), 1);
      return { km, miles, usable: true };
    } else {
      return { km: 0, miles: 0, usable: false };
    }
  } catch (e) {
    console.log("error while calculating distance", e);
    return { km: 0, miles: 0, usable: false };
  }
}

function round(value, precision) {
  var multiplier = Math.pow(10, precision || 0);
  return Math.round(value * multiplier) / multiplier;
}

function displaySlotsByStep2(
  step,
  sm,
  tmap,
  serviceDuration,
  availableIntervals,
) {
  let stepstarted = false;
  const steplen = step / SLOTUNITMINS;
  const serviceDurationlen = serviceDuration / SLOTUNITMINS;
  let returnStartTimes = [];

  availableIntervals.forEach((availableInterval) => {
    const foundStartTimes = [];
    let referenceStartIndex = availableInterval[0];
    for (let s = availableInterval[0]; s < availableInterval[1]; s++) {
      if (sm[s] === 1) {
        if (stepstarted) {
          //existing step
          for (let fst of foundStartTimes) {
            if (fst.live) fst.uc += 1;
          }
          if ((s - referenceStartIndex) % steplen === 0) {
            foundStartTimes.push({ st: tmap[s], uc: 1, live: true });
            stepstarted = true;
            referenceStartIndex = s;
          }
        } else {
          //new step started
          foundStartTimes.push({ st: tmap[s], uc: 1, live: true });
          stepstarted = true;
          referenceStartIndex = s;
        }
      } else {
        for (let fst of foundStartTimes) {
          fst.live = false;
        }
        stepstarted = false;
      }
    }
    returnStartTimes = returnStartTimes.concat(
      foundStartTimes
        .filter((fst) => fst.uc >= serviceDurationlen)
        .map((fst) => fst.st),
    );
  });
  return returnStartTimes;
}

function buildTimeMap() {
  const tmap = new Array(SLOTSINADAY);
  for (let s = 0; s < SLOTSINADAY; s++) {
    const hr = Math.floor(s / SLOTSINHOUR);
    const mins = (s % SLOTSINHOUR) * SLOTUNITMINS;
    const tstr24 = `${hr < 10 ? "0" : ""}${hr}:${mins < 10 ? "0" : ""}${mins}`;
    const tfd =
      hr > 6 && hr < 12
        ? 1
        : hr > 11 && hr < 17
          ? 2
          : hr > 16 && hr <= 23
            ? 3
            : 4;
    const tstr12 = `${hr < 13 ? hr : hr - 12}:${mins < 10 ? "0" : ""}${mins} ${
      hr > 11 ? "PM" : "AM"
    }`;
    tmap[s] = { tstr24, tstr12, tfd, hr, mins };
  }
  return tmap;
}

//For UI : _getSchedules
const _getSchedules = async ({ companyId, providerId }) => {
  const response = await execReadBySortkey({
    opname: "listProviderSchedules",
    op: scheduleQueries.listAllScheduleItems,
    id: { id: getProviderScheduleId(companyId, providerId) },
    skey: { scheduleinfo: { beginsWith: "S" } },
    filter: { and: [{ active: { ne: false } }, { deleted: { ne: true } }] },
    limit: process.env.REACT_APP_LISTLIMIT,
  });

  //TODO: check if no items in the response

  const allSchedules = response.items.filter((schitem) =>
    schitem.scheduleinfo.startsWith("SC|"),
  );

  const allLocationIds = allSchedules.reduce(
    (agrigate, { locations = "[]" }) => [
      ...agrigate,
      ...JSON.parse(locations).map((lc) => stripLocationPrefix(lc)),
    ],
    [],
  );

  const uniqueLocations = uniq(allLocationIds);

  const allServices = response.items.filter((schitem) =>
    schitem.scheduleinfo.startsWith("SR|"),
  );
  const allSBs = response.items.filter((schitem) =>
    schitem.scheduleinfo.startsWith("SB|"),
  );

  //Get Locations
  const ORlocations = uniqueLocations.map((lId) => {
    return {
      id: {
        eq: lId,
      },
    };
  });
  const locationsFilter = {
    or: ORlocations,
  };
  const companyLocations = await execRead({
    opname: "listCompanyLocations",
    op: queries.listCompanyLocations,
    filter: locationsFilter,
    limit: process.env.REACT_APP_LISTLIMIT,
  });

  const pd = await getProviderDataForSchedules(providerId);

  const returnSchedules = allSchedules.map((schedule) => {
    const scheduleLocationIds = JSON.parse(schedule.locations).map((id) =>
      id.substring(3),
    );
    return {
      ...schedule,
      id: schedule.id + SCHEDPKSKSPLITAT + schedule.scheduleinfo, //to support DataTableWrapper
      SBs: allSBs
        .filter((tb) => tb.scheduleinfo.includes(schedule.scheduleinfo))
        .map((tb) => {
          createScheduleDescription(tb);
          return {
            ...tb,
            id: tb.id + SCHEDPKSKSPLITAT + tb.scheduleinfo,
          };
        }),
      companyLocations: companyLocations.items.filter(({ id }) =>
        scheduleLocationIds.includes(id),
      ),
      services: allServices
        .filter(
          (serv) =>
            serv.scheduleinfo.endsWith(schedule.scheduleinfo) ||
            serv.scheduleinfo.includes(schedule.scheduleinfo.split("|")[1]),
        )
        .map((serv) => {
          const servicetypeId = serv.scheduleinfo.split("|")[1];
          return {
            scheduleinfo: serv.scheduleinfo,
            servicetypeId,
            name: getServiceName(pd.servicetypes, servicetypeId),
          };
        }),
    };
  });
  return returnSchedules;
};

function getServiceName(providerServices, servicetypeId) {
  let serviceTypeName = null;
  providerServices.forEach((st) => {
    if (servicetypeId === st.servicetype.id) {
      serviceTypeName = st.servicetype.name;
    }
  });
  return serviceTypeName;
}
export function getProviderScheduleId(cid, pid) {
  return `C-${cid}|P-${pid}`;
}

const getProviderDataForSchedules = async (providerid) => {
  const providerData = await API.graphql(
    graphqlOperation(scheduleQueries.providerDataForScheduleQuery, {
      id: providerid,
    }),
  );

  let providerDataForSchedule = {};

  if (
    providerData &&
    providerData.data &&
    providerData.data.getProvider &&
    providerData.data.getProvider.servicetypes &&
    providerData.data.getProvider.servicetypes.items
  ) {
    providerDataForSchedule = {
      ...providerDataForSchedule,
      servicetypes: providerData.data.getProvider.servicetypes.items.filter(
        (s) => !(true === s.servicetype.deleted),
      ),
    };
  }
  if (
    providerData &&
    providerData.data &&
    providerData.data.getProvider &&
    providerData.data.getProvider.locations &&
    providerData.data.getProvider.locations.items
  ) {
    providerDataForSchedule = {
      ...providerDataForSchedule,
      locations: providerData.data.getProvider.locations.items.filter(
        (pl) => !(true === pl.deleted),
      ),
    };
  }

  return providerDataForSchedule;
};

async function checkPackageBookedSlotsValid(bookingState) {
  const slots = bookingState.heldPackageSlots;
  const bookings = bookingState.packageBookedSlots;
  if (slots?.length !== bookings?.length) {
    return false;
  }
  //if each slot exists in the db
  for (let slot of slots) {
    const idsplit = slot.id.split(SCHEDPKSKSPLITAT);
    const result = await API.graphql(
      graphqlOperation(
        /* GraphQL */
        `
          query GetProviderSchedule($id: ID!, $scheduleinfo: String!) {
            getProviderSchedule(id: $id, scheduleinfo: $scheduleinfo) {
              id
              scheduleinfo
            }
          }
        `,
        {
          id: idsplit[0],
          scheduleinfo: idsplit[1],
        },
      ),
    );
    return result.data.getProviderSchedule?.scheduleinfo === slot.scheduleinfo;
  }
  // check if order of slots and bookings match in the respective arrays
  let index = 0;
  for (let b of bookings) {
    if (b.dateInfo.dtstamp_str !== slots[index]?.sdtutc) {
      return false;
    }
    index = index + 1;
  }
  return true;
}
async function checkSelectedSlotStillAvailable(bookingState) {
  const pksk = getProviderScheduleId(
    bookingState.companyId,
    bookingState.provider.id,
  );
  let startDate = new Date(bookingState.selectedslot.date);
  startDate = getAWSDate(startDate);
  let startTime = bookingState.selectedslot.label;
  const bookedResp = await execReadBySortkey({
    opname: "listProviderSchedules",
    op: scheduleQueries.listTimeblocks,
    id: { id: pksk },
    skey: {
      scheduleinfo: { beginsWith: `BK|${startDate} ${startTime}` },
    },
    filter: { status: { ne: "CANCELLED" } },
    limit: 500,
  });

  if (bookedResp && bookedResp.items) {
    if (bookedResp.items.length) {
      const locationTz = bookingState.serviceLocationObj.timezone;

      let allSlotsHaveTz = true;
      let slotTzMatch = false;

      for (let slot of bookedResp.items) {
        if (!slot.tz) allSlotsHaveTz = false;
        if (slot.tz === locationTz) {
          slotTzMatch = true;
        }
      }
      if (allSlotsHaveTz) return !slotTzMatch;

      return false;
    }
  }
  return true;
}

async function checkSlotStillAvailablePkgBkgFlow(
  selectedslot,
  pk,
  { timezone: locationTz },
) {
  let startDate = new Date(selectedslot.date);
  startDate = getAWSDate(startDate);
  let startTime = selectedslot.label;
  const bookedResp = await execReadBySortkey({
    opname: "listProviderSchedules",
    op: scheduleQueries.listTimeblocks,
    id: { id: pk },
    skey: {
      scheduleinfo: { beginsWith: `BK|${startDate} ${startTime}` },
    },
    filter: { status: { ne: "CANCELLED" } },
    limit: 500,
  });

  if (bookedResp && bookedResp.items) {
    if (bookedResp.items.length) {
      let allSlotsHaveTz = true;
      let slotTzMatch = false;

      for (let slot of bookedResp.items) {
        if (!slot.tz) allSlotsHaveTz = false;
        if (slot.tz === locationTz) {
          slotTzMatch = true;
        }
      }
      if (allSlotsHaveTz) return !slotTzMatch;

      return false;
    }
  }
  return true;
}

function getPhysicalLocationGeoLoc(serviceLocationObj) {
  if (serviceLocationObj) {
    let locObj = serviceLocationObj;
    if (locObj.latitude && locObj.longitude) {
      return {
        lat: locObj.latitude,
        lng: locObj.longitude,
      };
    }
  }
  return;
}

async function getClusteredSlots(
  bookingState,
  lookAheadDays = DEFAULT_LOOKAHEAD_DAYS_FOR_CLUSTERING,
  startDate = new Date(),
  includeFullDayAnyTimeSlots = false,
  locationTz,
  forceBookingTimeWithPackageBookingTime,
) {
  try {
    let timeSuggestionsEnabled = false;
    let firstBookingAnchorTime;
    let maxTravelTimeMinutes;
    let canProviderSetMaxTravelTime = false;

    if (bookingState && bookingState.company?.ProviderRestrictionConfig) {
      const providerRestrictionConfig = JSON.parse(
        bookingState.company.ProviderRestrictionConfig || "{}",
      );

      canProviderSetMaxTravelTime =
        providerRestrictionConfig.canProviderSetMaxTravelTime;
    }
    if (bookingState && bookingState.company?.SuggestionConfig) {
      const suggestionConfigObj = JSON.parse(
        bookingState.company.SuggestionConfig,
      );
      timeSuggestionsEnabled = suggestionConfigObj.isEnabled;
      firstBookingAnchorTime = suggestionConfigObj.timeOfFirstBookingOfTheDay;
      maxTravelTimeMinutes = suggestionConfigObj.maxTravelTimeMinutes;
    }
    if (canProviderSetMaxTravelTime && bookingState.provider.maxTravelTime)
      maxTravelTimeMinutes = bookingState.provider.maxTravelTime;
    if (timeSuggestionsEnabled) {
      let geoLoc = bookingState.isRemoteLocation
        ? bookingState.remoteAddressCoordinates
        : getPhysicalLocationGeoLoc(bookingState.serviceLocationObj);
      const timeZoneAdjustedScheduleBlocks = adjustScheduleBlocksToLocationTz(
        bookingState.provider.timezone,
        locationTz,
        bookingState.provider.SBs,
      );
      if (geoLoc) {
        const clusteredSlotsResponse = await API.post(
          "bookingapi",
          "/clusteredslots",
          {
            body: {
              companyId: bookingState.company.id,
              providerId: bookingState.provider.id,
              providerSBs: timeZoneAdjustedScheduleBlocks,
              startdate: getAWSDate(startDate),
              numdays: lookAheadDays,
              bookingIntervalMinutes:
                bookingState.company.bookingIntervalMinutes,
              serviceDuration: bookingState.serviceType.minutes,
              maxTravelTime: maxTravelTimeMinutes, // Provider.maxTravelTime (when becomes available) or company.SuggestionConfig.maxTravelTimeMinutes
              geoLoc,
              bookingIncrement: 15,
              BROWSER_TZ: Intl.DateTimeFormat().resolvedOptions().timeZone, //BROWSER_TZ
              firstBookingAnchorTime: firstBookingAnchorTime,
              includeFullDayAnyTimeSlots,
              locationTz,
              forceBookingTime: forceBookingTimeWithPackageBookingTime
                ? forceBookingTimeWithPackageBookingTime
                : "",
            },
          },
        );
        //ensure unique slots
        if (clusteredSlotsResponse && clusteredSlotsResponse.clusteredslots) {
          if (clusteredSlotsResponse.clusteredslots.slots?.length) {
            const seenDates = new Set();
            clusteredSlotsResponse.clusteredslots.slots =
              clusteredSlotsResponse.clusteredslots.slots.filter((obj) => {
                if (seenDates.has(obj.clusteredReadableSlot.datetime)) {
                  return false;
                } else {
                  seenDates.add(obj.clusteredReadableSlot.datetime);
                  return true;
                }
              });
          }
        }
        return clusteredSlotsResponse;
      }
    }
  } catch (e) {
    console.log("getClusteredSlots error:", e);
  }
}
async function getTimeSuggestions(
  locationTz,
  bookingState,
  lookAheadDays = DEFAULT_LOOKAHEAD_DAYS_FOR_CLUSTERING,
  startDate = new Date(),
  maxNumberOfSlots = DEFAULT_MAX_NUM_OF_SUGGESTED_SLOTS_TO_DISPALY,
) {
  try {
    const result = await getClusteredSlots(
      bookingState,
      lookAheadDays,
      startDate,
      false,
      locationTz,
    );
    if (result) {
      if (result && result.clusteredslots) {
        // sort first
        result.clusteredslots.slots.sort((slot1, slot2) => {
          const slot1DateTime = new Date(
            slot1.clusteredReadableSlot?.datetime,
          ).valueOf();
          const slot2DateTime = new Date(
            slot2.clusteredReadableSlot?.datetime,
          ).valueOf();
          if (slot1DateTime > slot2DateTime) return 1;
          else if (slot1DateTime < slot2DateTime) return -1;
          else return 0;
        });
        if (result.clusteredslots.slots?.length > maxNumberOfSlots)
          result.clusteredslots.slots = result.clusteredslots.slots.slice(
            0,
            maxNumberOfSlots,
          );
        return result.clusteredslots;
      }
    } else {
      return {
        slots: [],
        fullDays: [],
      };
    }
  } catch (e) {
    console.log(e);
    return {
      slots: [],
      fullDays: [],
    };
  }
}

async function getPackageSuggestedTimes(apptDates, bookingState, locationTz) {
  try {
    if (!apptDates || apptDates.length === 0) return [];

    // Determine the full date range across all apptDates
    const allDates = apptDates.map((a) => new Date(a.date));
    const minDate = new Date(Math.min(...allDates));
    const maxDate = new Date(Math.max(...allDates));
    // Add a day buffer to maxDate to ensure the last date is included
    maxDate.setDate(maxDate.getDate() + 1);

    const pad = (n) => String(n).padStart(2, "0");
    const startOffset = format(
      new Date(Date.UTC(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 12, 0, 0)),
      "XXX",
      { timeZone: locationTz },
    );
    const endOffset = format(
      new Date(Date.UTC(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 12, 0, 0)),
      "XXX",
      { timeZone: locationTz },
    );
    const startDateIso = `${minDate.getFullYear()}-${pad(minDate.getMonth() + 1)}-${pad(minDate.getDate())}T00:00:00${startOffset}`;
    const endDateIso = `${maxDate.getFullYear()}-${pad(maxDate.getMonth() + 1)}-${pad(maxDate.getDate())}T23:59:59${endOffset}`;

    const geoLoc = bookingState.isRemoteLocation
      ? bookingState.remoteAddressCoordinates
      : getPhysicalLocationGeoLoc(bookingState.serviceLocationObj);

    // Single V2 API call for the entire date range
    const response = await getClusteredSlotsV2({
      companyId: bookingState.company.id,
      providerId: bookingState.provider.id,
      serviceId: bookingState.serviceType.id,
      geoLoc,
      isCompanyLocation: !bookingState.isRemoteLocation,
      companyLocationId: !bookingState.isRemoteLocation
        ? bookingState.locationId
        : null,
      startDate: startDateIso,
      endDate: endDateIso,
      timezone: locationTz,
      isPackage: true,
      timeSuggestionsLimit: apptDates.length * 50,
    });

    // Build a display map of available slots grouped by date
    const dm = transformClustersResponseToDisplayMap(
      response,
      locationTz,
      bookingState.provider.id,
    );

    // Match each apptDate to the closest available slot
    const suggestedPackageDates = [];
    for (const apptDate of apptDates) {
      const apptDateObj = new Date(apptDate.date);
      const zonedApptDate = utcToZonedTime(apptDateObj, locationTz);
      const dateKey = format(zonedApptDate, "yyMMdd");
      const dayEntry = dm[dateKey];

      if (!dayEntry || !dayEntry.slots || dayEntry.slots.length === 0) {
        // No slots on this date — skip (will be filtered out as invalid)
        continue;
      }

      const apptTimeMs = apptDateObj.getTime();

      // Find exact match or closest slot
      let bestSlot = null;
      let bestDiff = Infinity;
      for (const slot of dayEntry.slots) {
        const diff = Math.abs(slot.datetime.getTime() - apptTimeMs);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestSlot = slot;
        }
      }

      if (bestSlot) {
        const isExactMatch = bestDiff === 0;
        suggestedPackageDates.push({
          date: bestSlot.datetime,
          key: bestSlot.datetime.toISOString(),
          selected: true,
          validity: {
            isValid: isExactMatch,
            hasAlternate: !isExactMatch,
            originalSlot: apptDate.date,
            dateInSchedule: true,
            ...(!isExactMatch && {
              alternateSlot: {
                date: bestSlot.datetime,
                slot: bestSlot,
              },
            }),
          },
        });
      }
    }
    return suggestedPackageDates;
  } catch (e) {
    console.log("error in getPackageSuggestedTimes", e);
    return [];
  }
}
export const searchProviderSchedulesByAddress = async (
  coords,
  providerId,
  serviceTypeIds,
  companyId,
) => {
  try {
    let result = await API.get("searchapi", "/search", {
      queryStringParameters: {
        providerId,
        lng: coords.lng,
        lat: coords.lat,
        stids: serviceTypeIds,
        companyId,
      },
    });

    if (result && result.length > 0) {
      return result.map((esr) => {
        return {
          id: esr.id,
          active: esr.active,
          scheduleinfo: esr.scheduleinfo,
        };
      });
    } else return [];
  } catch (ex) {
    console.log("error", ex);
    return [];
  }
};

export const compareTimezones = (tz1, tz2) => {
  const nowDate = new Date();
  console.log(
    utcToZonedTime(nowDate, tz1) > utcToZonedTime(nowDate, tz2),
    utcToZonedTime(nowDate, tz1) < utcToZonedTime(nowDate, tz2),
  );
  return utcToZonedTime(nowDate, tz1) > utcToZonedTime(nowDate, tz2);
};

export function sortProvidersByRating(providers) {
  return providers.sort((p1, p2) => {
    let r1 = p1.ratingstarsavg ? p1.ratingstarsavg : 0;
    let r2 = p2.ratingstarsavg ? p2.ratingstarsavg : 0;
    if (r1 > r2) return -1;
    if (r1 < r2) return 1;
    let n1 = `${p1.firstname}${p1.lastname}`.toUpperCase();
    let n2 = `${p2.firstname}${p2.lastname}`.toUpperCase();
    if (n1 > n2) return 1;
    if (n1 < n2) return -1;
    return 0;
  });
}

function calculateDailyPossibleMaxAvailability(availableIntervals) {
  if (availableIntervals) {
    let counti = 0;
    for (let interval of availableIntervals) {
      counti += interval[1] - interval[0];
    }
    return counti * SLOTUNITMINS;
  }
  return 0;
}
export function calculateDailyStats(unreadables) {
  let dailyStats = [];
  if (unreadables) {
    for (let d of unreadables) {
      if (d.hasavail) {
        let tfd = new Set();
        let slotlengths = d.slotmap.reduce(
          function (sl, s, i) {
            if (s) {
              sl.currLen++;
              sl.total++;
              sl.live = true;
              tfd.add(TIMEMAP[i].tfd);
            } else {
              if (sl.live) {
                if (sl.currLen > sl.currMaxLen) {
                  sl.currMaxLen = sl.currLen;
                }
                if (sl.currMinLen > sl.currLen) {
                  sl.currMinLen = sl.currLen;
                }
                sl.currLen = 0;
                sl.live = false;
              }
            }
            return sl;
          },
          { currLen: 0, currMinLen: 288, currMaxLen: 0, total: 0, live: false },
        );
        const dailyPossibleMaxAvil = calculateDailyPossibleMaxAvailability(
          d.availableIntervals,
        );
        dailyStats.push({
          d: moment(d.date).format("YYYY-MM-DD"),
          p:
            dailyPossibleMaxAvil === 0
              ? 0
              : ((slotlengths.total * SLOTUNITMINS) / dailyPossibleMaxAvil) *
                100,
          sxl: slotlengths.currMaxLen * SLOTUNITMINS,
          m: tfd.has(1) ? 1 : 0,
          a: tfd.has(2) ? 1 : 0,
          e: tfd.has(3) ? 1 : 0,
          stl: slotlengths.total * SLOTUNITMINS,
        });
      }
    }
  }
  return dailyStats;
}

//function to sanitize schedule blocks by remove "duplicate" and expired ones
const sanitizeSBs = (sbs) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); //normalize to start of day

  //filter out expired and inactive SBs
  const activeSBs = sbs.filter((sb) => {
    const endDate = new Date(`${sb.endDate}T${sb.endTime}`);
    return endDate >= today && sb.active !== false;
  });

  //keep track of unique repeat & timeRange combinations
  const seen = new Set();
  const uniqueSBs = [];

  activeSBs.forEach((sb) => {
    const key = `${sb.repeat || ""}|${sb.timeRange}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueSBs.push(sb);
    }
  });

  return uniqueSBs;
};

async function fetchProviderSchedulesInBatches(providers) {
  const results = [];

  //process the providers in batches
  for (let i = 0; i < providers.length; i += BATCH_SIZE) {
    const batch = providers.slice(i, i + BATCH_SIZE);

    const batchPromises = batch.map((provider) => {
      const { id, scheduleinfo } = provider.schedule;
      return API.graphql(
        graphqlOperation(
          `query GetProviderSchedule($id: ID!, $scheduleinfo: String!) {
                    getProviderSchedule(id: $id, scheduleinfo: $scheduleinfo) {
                      id
                      scheduleinfo
                      locations
                    }
                  }
                `,
          {
            id,
            scheduleinfo,
          },
        ),
      )
        .then((response) => ({
          providerId: provider.id,
          data: response.data.getProviderSchedule,
        }))
        .catch((error) => ({
          providerId: provider.id,
          error: error.message,
        }));
    });
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
  }

  return results;
}

async function fetchProviderLocationsInBatches(locationIds) {
  const results = [];

  for (let i = 0; i < locationIds.length; i += BATCH_SIZE) {
    const batch = locationIds.slice(i, i + BATCH_SIZE);

    const batchPromises = batch.map((locationId) =>
      API.graphql(
        graphqlOperation(
          `query GetProviderLocation($id: ID!) {
          getProviderLocation(id: $id) {
            id
            providerId
            latitude
            longitude
          }
        }`,
          {
            id: locationId,
          },
        ),
      )
        .then((response) => ({
          locationId,
          data: response.data.getProviderLocation,
        }))
        .catch((error) => ({
          locationId,
          error: error.message,
        })),
    );

    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
  }

  return results;
}

/**
 * Calculates and attaches distance information between providers' locations and a booking location.
 *
 * This function performs a multi-step process:
 * 1. Fetches schedule information for all providers in parallel batches
 * 2. Extracts physical location IDs (PL-) from each provider's schedule
 * 3. Fetches geographic coordinates for each physical location in parallel batches
 * 4. Calculates distances using the Haversine formula
 * 5. Returns the original providers array with distance information attached
 *
 * @param {Array<Object>} providers - Array of provider objects containing schedule information
 * @param {Object} bookingGeoLocation - The booking location coordinates { lat: number, lng: number }
 * @param {string} [distanceUnit="kilometers"] - Unit for distance calculation ("kilometers" or "miles")
 * @returns {Promise<Array<Object>>} - Original providers array with added 'distance' property (rounded to 1 decimal)
 */
async function getProviderScheduleDistance(
  providers,
  bookingGeoLocation,
  distanceUnit = "kilometers",
) {
  try {
    // Step 1: Fetch all provider schedules in parallel batches
    const scheduleResults = await fetchProviderSchedulesInBatches(providers);

    // Step 2: Extract PL- locations and create mapping - this is so we can effiently fetch all locations in parallel
    const providerLocationMap = new Map();
    const locationIds = [];
    scheduleResults.forEach((result) => {
      if (!result.error && result.data?.locations) {
        try {
          const locations = JSON.parse(result.data.locations);
          const plLocation = locations.find((loc) => loc.startsWith("PL-"));

          if (plLocation) {
            const locationId = plLocation.replace("PL-", "");
            providerLocationMap.set(result.providerId, locationId);
            locationIds.push(locationId);
          }
        } catch (e) {
          console.error(
            `Error parsing locations for provider ${result.providerId}:`,
            e,
          );
        }
      }
    });

    // Step 3: Fetch all provider locations in parallel batches
    const locationResults = await fetchProviderLocationsInBatches(locationIds);

    // Step 4: Create location data map so key is locationId and value is lat/lng
    const locationDataMap = new Map();

    locationResults.forEach((result) => {
      if (!result.error && result.data) {
        locationDataMap.set(result.locationId, {
          lat: result.data.latitude,
          lng: result.data.longitude,
        });
      }
    });

    //Step 5: Calculate distances using the turf distance function (haversine formula) and attach the distance to the providers
    const providersWithDistance = providers.map((provider) => {
      const locationId = providerLocationMap.get(provider.id);
      if (!locationId) return { ...provider, distance: null };

      const location = locationDataMap.get(locationId);
      if (!location) return { ...provider, distance: null };

      const distance = calculateTurfDistance(
        [bookingGeoLocation.lng, bookingGeoLocation.lat],
        [location.lng, location.lat],
        distanceUnit,
      );

      return {
        ...provider,
        distance: Math.round(distance * 10) / 10, //1 decimal place
      };
    });

    return providersWithDistance;
  } catch (error) {
    console.error("Error in getProviderScheduleDistance:", error);
    return providers;
  }
}

async function checkHeldSlotsRecrodsExist(heldSlots) {
  //if each slot exists in the db
  for (let slot of heldSlots) {
    const idsplit = slot.id.split(SCHEDPKSKSPLITAT);
    const result = await API.graphql(
      graphqlOperation(
        /* GraphQL */
        `
          query GetProviderSchedule($id: ID!, $scheduleinfo: String!) {
            getProviderSchedule(id: $id, scheduleinfo: $scheduleinfo) {
              id
              scheduleinfo
            }
          }
        `,
        {
          id: idsplit[0],
          scheduleinfo: idsplit[1],
        },
      ),
    );
    if (!result.data.getProviderSchedule?.scheduleinfo) return false;
  }
  return true;
}

const getCourseProvider = async (selectedCourseDate) => {
  const data = await API.graphql(
    graphqlOperation(
      `query GetProvider($id: ID!) {
      getProvider(id: $id) {
         id
        firstname
        lastname
        emailaddress
        phone
        addressoneline
        street
        city
        state
        country
        postalcode
        longitude
        latitude
        timezone
        bio
        pictures3key
        maxtraveltype
        companyId
        active
        deleted
        ratingstarsavg
        numberofratings
        AcceptanceRatios
        offersVirtualServices
        virtualMeetingUserId
        vmlink
        permalink
        company {
          displayProviderRatios
          hideRatingsFromClients
          useAnonymousPermalink
        }
        locations {
          items {
            id
            travelregions
            traveldistance
            traveldistanceunit
            maxtraveltype
            longitude
            latitude
          }
          nextToken
        }
        servicetypes {
          items {
            id
            servicetype {
              id
              name
              desc
              categoryId
              categoryName
              category {
                id
                name
                ordinal
              }
              locations
              minutes
              price
              active
              taxexempt
              TaxOverride
              ordinal
              deleted
              companyId
              behavior
              Classification
              allowRecurring
              CourseSettings
            }
          }
          nextToken
        }
        skills {
          items {
            id
            skill {
              id
              name
              active
              deleted
            }
          }
          nextToken
        }
      }
    }`,
      {
        id: selectedCourseDate.providerId,
      },
    ),
  );
  return data.data.getProvider;
};

const fetchCourseDates = async ({
  companyId,
  multipleServices,
  isProviderAgnostic,
}) => {
  try {
    if (multipleServices || isProviderAgnostic) return;
    const { allAssignments } = await API.get(
      "serviceManagement",
      `/service/company/${companyId}/coursedates`,
    );
    return allAssignments;
  } catch (e) {
    console.log(e);
  }
};

async function getClusteredSlotsV2({
  companyId,
  providerId,
  serviceId,
  geoLoc,
  isCompanyLocation,
  companyLocationId,
  startDate,
  endDate,
  timezone,
  isPackage,
  timeSuggestionsLimit = 100,
}) {
  try {
    const response = await API.post(
      "MB-rest-api",
      `/v1/mb/search/slots?timeSuggestionsLimit=${timeSuggestionsLimit}`,
      {
        body: {
          companyId,
          providers: [providerId],
          targetServiceId: serviceId,
          targetGeoLocation: geoLoc ? [geoLoc.lat, geoLoc.lng] : [],
          isCompanyLocation: !!isCompanyLocation,
          companyLocationId: isCompanyLocation ? companyLocationId : null,
          startDate,
          endDate,
          timezone,
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          skills: [],
          recurring: false,
          rrule: "FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU,WE,TH,FR,SA,SU",
          package: !!isPackage,
          distanceUnit: "mi",
        },
      },
    );
    return response;
  } catch (e) {
    console.log("getClusteredSlotsV2 error:", e);
    return { timeSlots: [], openSlots: {} };
  }
}

function transformClustersResponseToDisplayMap(
  response,
  locationTz,
  providerId,
) {
  const dm = {};
  if (!response || !response.openSlots) return dm;

  const providerSlots = response.openSlots[providerId] || [];
  for (const slot of providerSlots) {
    const utcDate = new Date(slot.timeSlotDateAndTime);
    const zonedDate = utcToZonedTime(utcDate, locationTz);
    const dateKey = format(zonedDate, "yyMMdd");
    const hr = zonedDate.getHours();
    const mins = zonedDate.getMinutes();
    const start24 = `${hr < 10 ? "0" : ""}${hr}:${mins < 10 ? "0" : ""}${mins}`;
    const ampm = hr >= 12 ? "PM" : "AM";
    const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    const start12 = `${hr12}:${mins < 10 ? "0" : ""}${mins} ${ampm}`;
    const tfd =
      hr > 6 && hr < 12
        ? 1
        : hr > 11 && hr < 17
          ? 2
          : hr > 16 && hr <= 23
            ? 3
            : 4;

    const dayDate = new Date(
      zonedDate.getFullYear(),
      zonedDate.getMonth(),
      zonedDate.getDate(),
    );

    if (!dm[dateKey]) {
      dm[dateKey] = {
        date: dayDate,
        slots: [],
      };
    }
    const datetime = new Date(dayDate.getTime());
    datetime.setHours(hr);
    datetime.setMinutes(mins);

    dm[dateKey].slots.push({
      start24,
      start12,
      datetime,
      hr,
      tfd,
      len: slot.serviceDuration || 0,
    });
  }

  // Sort slots within each day
  for (const key of Object.keys(dm)) {
    dm[key].slots.sort((a, b) => a.datetime.valueOf() - b.datetime.valueOf());
  }

  return dm;
}

function transformTimeSlots(timeSlots, locationTz) {
  if (!timeSlots || !Array.isArray(timeSlots)) return [];

  return timeSlots.map((slot) => {
    const utcDate = new Date(slot.timeSlotDateAndTime);
    const zonedDate = utcToZonedTime(utcDate, locationTz);
    const hr = zonedDate.getHours();
    const mins = zonedDate.getMinutes();
    const start24 = `${hr < 10 ? "0" : ""}${hr}:${mins < 10 ? "0" : ""}${mins}`;
    const ampm = hr >= 12 ? "PM" : "AM";
    const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    const start12 = `${hr12}:${mins < 10 ? "0" : ""}${mins} ${ampm}`;
    const tfd =
      hr > 6 && hr < 12
        ? 1
        : hr > 11 && hr < 17
          ? 2
          : hr > 16 && hr <= 23
            ? 3
            : 4;

    const dayDate = new Date(
      zonedDate.getFullYear(),
      zonedDate.getMonth(),
      zonedDate.getDate(),
    );
    const datetime = new Date(dayDate.getTime());
    datetime.setHours(hr);
    datetime.setMinutes(mins);

    return {
      date: dayDate,
      datetime,
      start24,
      start12,
      hr,
      tfd,
      preferredTimeSlot: slot.preferredTimeSlot,
      travelTimeMins: slot.travelTimeMins,
      travelType: slot.travelType,
      message: slot.message,
    };
  });
}

async function getClusteredSlotsForAutoProvider({
  companyId,
  serviceId,
  geoLoc,
  isCompanyLocation,
  companyLocationId,
  startDate,
  endDate,
  timezone,
  isPackage,
}) {
  try {
    const response = await API.post(
      "MB-rest-api",
      "/v1/mb/search/slots?timeSuggestionsLimit=100",
      {
        body: {
          companyId,
          // No providers array - this gets best slots across all available providers
          targetServiceId: serviceId,
          targetGeoLocation: geoLoc ? [geoLoc.lat, geoLoc.lng] : [],
          isCompanyLocation: !!isCompanyLocation,
          companyLocationId: isCompanyLocation ? companyLocationId : null,
          startDate,
          endDate,
          timezone,
          daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
          skills: [],
          recurring: false,
          rrule: "FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU,WE,TH,FR,SA,SU",
          package: !!isPackage,
          distanceUnit: "mi",
        },
      },
    );
    return response;
  } catch (e) {
    console.log("getClusteredSlotsForAutoProvider error:", e);
    return { timeSlots: [], openSlots: {} };
  }
}

function transformAutoProviderResponse(response, locationTz) {
  const result = {
    displayMap: {},
    clusteredTimeSlots: [],
    bestProviderId: null,
    providerSlotCounts: {},
  };

  if (!response) return result;

  // Transform timeSlots (suggested times) - get unique slots by time
  if (response.timeSlots && Array.isArray(response.timeSlots)) {
    const seenTimes = new Set();
    const uniqueTimeSlots = [];

    for (const slot of response.timeSlots) {
      const utcDate = new Date(slot.timeSlotDateAndTime);
      const zonedDate = utcToZonedTime(utcDate, locationTz);
      const timeKey = `${zonedDate.getFullYear()}-${zonedDate.getMonth()}-${zonedDate.getDate()}-${zonedDate.getHours()}-${zonedDate.getMinutes()}`;

      if (!seenTimes.has(timeKey)) {
        seenTimes.add(timeKey);
        const hr = zonedDate.getHours();
        const mins = zonedDate.getMinutes();
        const start24 = `${hr < 10 ? "0" : ""}${hr}:${mins < 10 ? "0" : ""}${mins}`;
        const ampm = hr >= 12 ? "PM" : "AM";
        const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
        const start12 = `${hr12}:${mins < 10 ? "0" : ""}${mins} ${ampm}`;
        const tfd =
          hr > 6 && hr < 12
            ? 1
            : hr > 11 && hr < 17
              ? 2
              : hr > 16 && hr <= 23
                ? 3
                : 4;

        const dayDate = new Date(
          zonedDate.getFullYear(),
          zonedDate.getMonth(),
          zonedDate.getDate(),
        );
        const datetime = new Date(dayDate.getTime());
        datetime.setHours(hr);
        datetime.setMinutes(mins);

        uniqueTimeSlots.push({
          date: dayDate,
          datetime,
          start24,
          start12,
          hr,
          tfd,
          preferredTimeSlot: slot.preferredTimeSlot,
          travelTimeMins: slot.travelTimeMins,
          travelType: slot.travelType,
          message: slot.message,
          providerId: slot.providerId,
        });
      }
    }
    result.clusteredTimeSlots = uniqueTimeSlots;
  }

  // Find provider with most slots
  if (response.openSlots) {
    let maxSlots = 0;
    for (const [providerId, slots] of Object.entries(response.openSlots)) {
      result.providerSlotCounts[providerId] = slots.length;
      if (slots.length > maxSlots) {
        maxSlots = slots.length;
        result.bestProviderId = providerId;
      }
    }

    // Transform open slots for the best provider only
    if (result.bestProviderId) {
      const providerSlots = response.openSlots[result.bestProviderId] || [];
      for (const slot of providerSlots) {
        const utcDate = new Date(slot.timeSlotDateAndTime);
        const zonedDate = utcToZonedTime(utcDate, locationTz);
        const dateKey = format(zonedDate, "yyMMdd");
        const hr = zonedDate.getHours();
        const mins = zonedDate.getMinutes();
        const start24 = `${hr < 10 ? "0" : ""}${hr}:${mins < 10 ? "0" : ""}${mins}`;
        const ampm = hr >= 12 ? "PM" : "AM";
        const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
        const start12 = `${hr12}:${mins < 10 ? "0" : ""}${mins} ${ampm}`;
        const tfd =
          hr > 6 && hr < 12
            ? 1
            : hr > 11 && hr < 17
              ? 2
              : hr > 16 && hr <= 23
                ? 3
                : 4;

        const dayDate = new Date(
          zonedDate.getFullYear(),
          zonedDate.getMonth(),
          zonedDate.getDate(),
        );

        if (!result.displayMap[dateKey]) {
          result.displayMap[dateKey] = {
            date: dayDate,
            slots: [],
          };
        }
        const datetime = new Date(dayDate.getTime());
        datetime.setHours(hr);
        datetime.setMinutes(mins);

        result.displayMap[dateKey].slots.push({
          start24,
          start12,
          datetime,
          hr,
          tfd,
          len: slot.serviceDuration || 0,
          providerId: result.bestProviderId,
        });
      }

      // Sort slots within each day
      for (const key of Object.keys(result.displayMap)) {
        result.displayMap[key].slots.sort(
          (a, b) => a.datetime.valueOf() - b.datetime.valueOf(),
        );
      }
    }
  }

  return result;
}

export {
  createTimeblock,
  awsDateToJsDate, // turns YYYY-MM-DD formated date into JS date object of browser local TZ
  getAWSDate, //returns a given date in YYYY-MM-DD format
  validateApptDate,
  _getSchedules,
  SCHEDPKSKSPLITAT,
  getSBsByScheduleInfo,
  fetchBUTs,
  getCompanyLocation,
  checkSelectedSlotStillAvailable,
  checkSlotStillAvailablePkgBkgFlow,
  calcDist,
  checkPackageBookedSlotsValid,
  getSlotsForSbs,
  calculateBookedMinutes,
  getEndingDate,
  getTimeSuggestions,
  getPackageSuggestedTimes,
  getPhysicalLocationGeoLoc,
  sortScheduleDisplayOrder,
  sanitizeSBs,
  fetchProviderSchedulesInBatches,
  getProviderScheduleDistance,
  checkHeldSlotsRecrodsExist,
  getCourseProvider,
  fetchCourseDates,
  getClusteredSlotsV2,
  transformClustersResponseToDisplayMap,
  transformTimeSlots,
  getClusteredSlotsForAutoProvider,
  transformAutoProviderResponse,
};
